import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import styles from '../Account.module.css';
import AccountCard from '../AccountCard';
import SectionHead from '../SectionHead';
import EmptyState from '../EmptyState';
import { fetchMemory, saveMemory, saveMemoryCached } from '../../../services/memoryService';
import { formatRevenueRange } from '../../../lib/revenueRange';

// What RepBuddy remembers about this rep, laid out so every item can be removed on its
// own. Edits go through PUT /api/memory as the whole blob; "Forget everything" is the one
// action that sends allowEmpty, so nothing else can blank the memory by accident.

const TALLY_SECTIONS = [
  ['titles',     'Titles you search for'],
  ['industries', 'Industries'],
  ['states',     'States'],
  ['companies',  'Companies you have searched'],
];

function tallyRows(tally) {
  return Object.entries(tally || {})
    .map(([key, v]) => ({ key, label: v?.display || key, n: v?.n || 0 }))
    .sort((a, b) => (b.n - a.n));
}

function Chip({ label, count, onRemove, disabled }) {
  return (
    <span className={styles.badge} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, margin: '0 8px 8px 0' }}>
      {label}{count != null && <span style={{ opacity: 0.6 }}>x{count}</span>}
      <button
        type="button"
        className={styles.btnGhost}
        style={{ padding: '0 4px', lineHeight: 1 }}
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Forget ${label}`}
        title="Forget this"
      >x</button>
    </span>
  );
}

export default function MemorySection() {
  const { user } = useAuth();
  const [memory,  setMemory]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState(false);
  const [msg,     setMsg]     = useState(null);   // { type: 'ok'|'err', text }
  const [avoidInput, setAvoidInput] = useState('');
  const [noteInput,  setNoteInput]  = useState('');
  const [confirmForget, setConfirmForget] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    const m = await fetchMemory();
    setMemory(m);
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Persist an edited blob. The Account page owns the whole object here, so it writes the
  // whole object back; the chat page never does this, it only sends events.
  async function commit(next, { allowEmpty = false, okText = 'Saved.' } = {}) {
    setBusy(true);
    setMsg(null);
    try {
      const saved = await saveMemory(next, { allowEmpty });
      setMemory(saved);
      saveMemoryCached(saved);
      setMsg({ type: 'ok', text: okText });
    } catch (err) {
      setMsg({ type: 'err', text: err.message });
    } finally {
      setBusy(false);
    }
  }

  const usual = memory?.usual || {};
  const hasTallies  = TALLY_SECTIONS.some(([f]) => Object.keys(usual[f] || {}).length > 0);
  const hasSettings = !!(usual.cap || usual.pool || usual.revenueRange);
  const avoid       = memory?.avoidCompanies || [];
  const notes       = memory?.notes || [];
  const taken       = tallyRows(memory?.taken?.companies).slice(0, 10);
  const isEmpty     = !hasTallies && !hasSettings && avoid.length === 0 && notes.length === 0 && taken.length === 0;

  function forgetValue(field, key) {
    const next = { ...memory, usual: { ...usual, [field]: { ...(usual[field] || {}) } } };
    delete next.usual[field][key];
    commit(next, { okText: 'Forgotten.' });
  }
  function forgetSingle(field) {
    commit({ ...memory, usual: { ...usual, [field]: null } }, { okText: 'Forgotten.' });
  }
  function removeAvoid(name) {
    commit({ ...memory, avoidCompanies: avoid.filter(a => a.name !== name) }, { okText: `${name} can show up again.` });
  }
  function addAvoid() {
    const name = avoidInput.trim();
    if (!name) return;
    if (avoid.some(a => a.name.toLowerCase() === name.toLowerCase())) { setAvoidInput(''); return; }
    setAvoidInput('');
    commit({ ...memory, avoidCompanies: [...avoid, { name, added: Date.now(), source: 'account' }] }, { okText: `${name} will not show up in searches.` });
  }
  function removeNote(text) {
    commit({ ...memory, notes: notes.filter(n => n.text !== text) }, { okText: 'Forgotten.' });
  }
  function addNote() {
    const text = noteInput.trim();
    if (!text) return;
    setNoteInput('');
    commit({ ...memory, notes: [...notes, { text, added: Date.now() }] }, { okText: 'Noted.' });
  }
  function forgetTaken(key) {
    const next = { ...memory, taken: { companies: { ...(memory?.taken?.companies || {}) } } };
    delete next.taken.companies[key];
    commit(next, { okText: 'Forgotten.' });
  }
  function forgetEverything() {
    setConfirmForget(false);
    commit({ version: 1 }, { allowEmpty: true, okText: 'RepBuddy has forgotten everything it learned about how you work.' });
  }

  return (
    <>
      <SectionHead
        title="Memory"
        subtitle="What RepBuddy has learned about how you work. It comes from your own searches, exports and pushes, and from what you tell it in chat. Only you can see it, and it never shapes anyone else's results."
      />

      {loading ? (
        <AccountCard title="Loading"><p className={styles.note}>Reading your memory…</p></AccountCard>
      ) : isEmpty ? (
        <AccountCard title="Nothing remembered yet">
          <EmptyState
            icon="·"
            title="RepBuddy has not learned anything about you yet"
            description="Run a couple of searches, or tell it something in chat like “remember that I only want Director level” or “don’t show me Artera again”, and it will show up here."
          />
        </AccountCard>
      ) : (
        <>
          <AccountCard
            title="How you usually search"
            description="Tallied from searches you actually ran. Something counts as usual once it has come up twice, or recently. Usual states are offered on the pre-search card, never applied on their own."
          >
            {hasTallies ? TALLY_SECTIONS.map(([field, label]) => {
              const rows = tallyRows(usual[field]);
              if (rows.length === 0) return null;
              return (
                <div key={field} style={{ marginBottom: 10 }}>
                  <div className={styles.fieldLabel}>{label}</div>
                  <div>
                    {rows.map(r => (
                      <Chip key={r.key} label={r.label} count={r.n} disabled={busy} onRemove={() => forgetValue(field, r.key)} />
                    ))}
                  </div>
                </div>
              );
            }) : <p className={styles.note} style={{ margin: 0 }}>No searches remembered yet.</p>}
          </AccountCard>

          <AccountCard title="Search settings" description="Pre-selected on the card before a search. You can always pick something else for one run.">
            {hasSettings ? (
              <div>
                {usual.cap && (
                  <Chip label={`${usual.cap.value} per company`} count={usual.cap.n} disabled={busy} onRemove={() => forgetSingle('cap')} />
                )}
                {usual.revenueRange && (
                  <Chip
                    label={usual.revenueRange.value === 'any' ? 'Any revenue' : `Revenue ${formatRevenueRange(usual.revenueRange.value)}`}
                    count={usual.revenueRange.n}
                    disabled={busy}
                    onRemove={() => forgetSingle('revenueRange')}
                  />
                )}
                {usual.pool && (
                  <Chip label={`${usual.pool.value === 'new' ? 'New pool' : usual.pool.value === 'existing' ? 'Existing results' : 'Both pools'}`} count={usual.pool.n} disabled={busy} onRemove={() => forgetSingle('pool')} />
                )}
              </div>
            ) : <p className={styles.note} style={{ margin: 0 }}>No usual cap, revenue or pool yet. RepBuddy learns these from what you pick on the card, or tell it in chat, like “remember I only sell to companies under $10M”.</p>}
          </AccountCard>

          <AccountCard title="Avoid list" description="Companies that never show up in broad searches. Naming one directly in a search still works.">
            <div>
              {avoid.map(a => (
                <Chip key={a.name} label={a.name} disabled={busy} onRemove={() => removeAvoid(a.name)} />
              ))}
              {avoid.length === 0 && <p className={styles.note} style={{ margin: '0 0 8px' }}>Nothing avoided yet.</p>}
            </div>
            <div className={styles.field} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                className={styles.input}
                value={avoidInput}
                onChange={e => setAvoidInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addAvoid(); }}
                placeholder="Company name to avoid"
                aria-label="Company name to avoid"
                disabled={busy}
              />
              <button type="button" className={`${styles.btnPrimary} ${styles.btnSmall}`} onClick={addAvoid} disabled={busy || !avoidInput.trim()}>Add</button>
            </div>
          </AccountCard>

          <AccountCard title="Notes" description="Things you asked RepBuddy to remember, in your words. It reads these before every reply.">
            <div>
              {notes.map(n => (
                <Chip key={n.text} label={n.text} disabled={busy} onRemove={() => removeNote(n.text)} />
              ))}
              {notes.length === 0 && <p className={styles.note} style={{ margin: '0 0 8px' }}>No notes yet.</p>}
            </div>
            <div className={styles.field} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                className={styles.input}
                value={noteInput}
                onChange={e => setNoteInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') addNote(); }}
                placeholder="e.g. Only Director level and above"
                aria-label="Note to remember"
                disabled={busy}
                maxLength={160}
              />
              <button type="button" className={`${styles.btnPrimary} ${styles.btnSmall}`} onClick={addNote} disabled={busy || !noteInput.trim()}>Add</button>
            </div>
          </AccountCard>

          {taken.length > 0 && (
            <AccountCard title="What you took with you" description="Companies whose contacts you pushed to HubSpot or exported. RepBuddy treats these as the ones that worked.">
              <div>
                {taken.map(r => (
                  <Chip key={r.key} label={r.label} count={r.n} disabled={busy} onRemove={() => forgetTaken(r.key)} />
                ))}
              </div>
            </AccountCard>
          )}
        </>
      )}

      {!loading && !isEmpty && (
        <AccountCard
          title="Forget everything"
          description="Clears every learned habit, the avoid list and your notes. Your seller profile is not affected."
          className={styles.dangerCard}
          actions={
            confirmForget ? (
              <>
                <button type="button" className={`${styles.btnGhost} ${styles.btnSmall}`} onClick={() => setConfirmForget(false)} disabled={busy}>Keep it</button>
                <button type="button" className={`${styles.btnDangerSolid} ${styles.btnSmall}`} onClick={forgetEverything} disabled={busy}>Yes, forget everything</button>
              </>
            ) : (
              <button type="button" className={`${styles.btnDanger} ${styles.btnSmall}`} onClick={() => setConfirmForget(true)} disabled={busy}>Forget everything</button>
            )
          }
        >
          <p className={styles.note} style={{ margin: 0 }}>RepBuddy will start learning again from your next search.</p>
        </AccountCard>
      )}

      {msg && (
        <p className={styles.note} style={{ margin: '10px 0 0', color: msg.type === 'ok' ? '#22c55e' : '#ef4444' }}>
          {msg.text}
        </p>
      )}
    </>
  );
}
